import { BodyText, Button, FormControl, Heading, Input } from '@farol-ds/react'
import { Image } from '@jusbrasil-web/shared-next-image'
import { useRef, useState } from 'react'
import { useIMask } from 'react-imask'

import illustration from './assets/phone-step-illustration.svg'
import styles from './phone-step.module.scss'

export interface PhoneStepProps {
  onActivate: (phoneDigits: string) => void
  onSkip: () => void
  isLoading?: boolean
  error?: string
}

const PHONE_DIGITS_VALID_LENGTHS = [10, 11]

const COPY = {
  title: 'Só falta ativar seus alertas',
  subtitle: 'Informe seu WhatsApp e receba avisos de processos e movimentações no seu CPF.',
  cta: 'Ativar alertas',
} as const

function isPhoneValid(digits: string): boolean {
  if (digits.length === 0) return false
  return PHONE_DIGITS_VALID_LENGTHS.includes(digits.length)
}

export function PhoneStep({ onActivate, onSkip, isLoading, error }: PhoneStepProps) {
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [phoneError, setPhoneError] = useState(false)

  const {
    ref: phoneRef,
    unmaskedValue: phoneUnmasked,
    value: phoneValue,
  } = useIMask(
    useRef({
      mask: '(00) 00000-0000',
      lazy: false,
      placeholderChar: '_',
    }).current
  )

  const copy = COPY

  function handlePhoneBlur() {
    setPhoneTouched(true)
    const digits = (phoneUnmasked || '').replace(/\D/g, '')
    if (digits.length > 0) setPhoneError(!isPhoneValid(digits))
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const digits = (phoneUnmasked || '').replace(/\D/g, '')
    setPhoneTouched(true)
    if (!isPhoneValid(digits)) {
      setPhoneError(true)
      return
    }
    onActivate(digits)
  }

  const hasError = (phoneTouched && phoneError) || !!error

  return (
    <div className={styles.container}>
      <div className={styles.textGroup}>
        <Image
          src={illustration}
          alt=""
          width={296}
          height={175}
          className={styles.illustration}
          unoptimized
        />
        <div className={styles.titleBlock}>
          <Heading size="lg" className={styles.title}>
            {copy.title}
          </Heading>
          <BodyText className={styles.subtitle}>{copy.subtitle}</BodyText>
        </div>
      </div>
      <form aria-label="phone-step" className={styles.form} onSubmit={handleSubmit} noValidate>
        <FormControl error={hasError}>
          <FormControl.Label>Telefone</FormControl.Label>
          <Input
            ref={phoneRef}
            type="tel"
            name="phone"
            value={phoneValue || ''}
            inputMode="tel"
            disabled={isLoading}
            onBlur={handlePhoneBlur}
          />
          <FormControl.ErrorMessage>
            {hasError && (error || 'Informe um número de telefone válido')}
          </FormControl.ErrorMessage>
        </FormControl>
        <Button type="submit" kind="primary" loading={isLoading} className={styles.ctaButton}>
          {copy.cta}
        </Button>
        <Button type="button" kind="plain" onClick={onSkip} disabled={isLoading}>
          Agora não
        </Button>
      </form>
    </div>
  )
}
